import { gql } from '@apollo/client';

export const DIAGNOSIS_TYPE_BASE = gql`
  fragment DiagnosisTypeBase on CarepathDiagnosisType {
    ajccMajorVersion
    emrDiagnosisKey
    id
    isPrimaryDiagnosis
    stageType
    draft
    disease {
      id
      diseaseId
      title
      chapter
    }
    stageTCodePathological {
      id
      subCode
      description
      mainCode
    }
    stageNCodePathological {
      id
      subCode
      description
      mainCode
    }
    stageMCodePathological {
      id
      subCode
      description
      mainCode
    }
    stageTCodeClinical {
      id
      subCode
      description
      mainCode
    }
    stageMCodeClinical {
      id
      subCode
      description
      mainCode
    }
    stageNCodeClinical {
      id
      subCode
      description
      mainCode
    }
    morphology {
      id
      displayName
      code
    }
    otherInfo
    laterality
    diagnosisDate
    diagnosisCode {
      id
      diagnosisCode
      diagnosisDescription
      stageNumber
      tumourStream {
        id
        name
      }
    }
  }
`;

export const DIAGNOSIS_TYPE_WITH_TREATMENT_STARTED = gql`
  fragment DiagnosisTypeWithTreatmentStarted on CarepathDiagnosisType {
    ...DiagnosisTypeBase
    hasTreatmentStarted
  }
  ${DIAGNOSIS_TYPE_BASE}
`;

export const CREATE_DIAGNOSIS = gql`
  mutation createDiagnosis($patientId: ID!, $diagnosisInput: DiagnosisInputType, $secondaryInput: DiagnosisInputType) {
    createDiagnosis(patientId: $patientId, diagnosisInput: $diagnosisInput, secondaryInput: $secondaryInput) {
      diagnosis {
        ...DiagnosisTypeBase
        relatedPrimaryDiagnosis {
          ...DiagnosisTypeBase
        }
      }
    }
  }
  ${DIAGNOSIS_TYPE_BASE}
`;

export const GET_PATIENT_DIAGNOSIS_LIST = gql`
  query getPatientDiagnosisList($patientId: ID!, $diagnosisType: String!, $secondaryCode: String) {
    diagnosisList(patientId: $patientId, diagnosisType: $diagnosisType, secondaryCode: $secondaryCode) {
      ...DiagnosisTypeBase
      relatedPrimaryDiagnosis {
        ...DiagnosisTypeBase
      }
    }
  }
  ${DIAGNOSIS_TYPE_BASE}
`;

export const CHECK_DIAGNOSIS_CAREPLAN_STATUS = gql`
  query CheckDiagnosisToCareplanStatus($diagnosisId: ID!, $careplanId: ID!) {
    checkDiagnosisToCareplanStatus(diagnosisId: $diagnosisId, careplanId: $careplanId) {
      id
      status
    }
  }
`;

export const GET_DIAGNOSIS_TREATMENT_STARTED = gql`
  query getDiagnosisTreatmentStarted($diagnosisId: ID!) {
    diagnosis(id: $diagnosisId) {
      id
      hasTreatmentStarted
    }
  }
`;

export const GET_DIAGNOSIS = gql`
  query getPatientDiagnosis($diagnosisId: ID!) {
    diagnosis(id: $diagnosisId) {
      ...DiagnosisTypeWithTreatmentStarted
      relatedPrimaryDiagnosis {
        ...DiagnosisTypeWithTreatmentStarted
      }
    }
    user {
      id
      isShowcaseUser
    }
  }
  ${DIAGNOSIS_TYPE_WITH_TREATMENT_STARTED}
`;

export const UPDATE_DIAGNOSIS = gql`
  mutation updateDiagnosis(
    $id: ID!
    $stageType: String
    $diagnosisCodeId: ID
    $stageTCodePathological: ID
    $stageNCodePathological: ID
    $stageMCodePathological: ID
    $stageTCodeClinical: ID
    $stageNCodeClinical: ID
    $stageMCodeClinical: ID
    $morphology: String
    $laterality: String
    $otherInfo: String
    $diagnosisDate: String
    $isPrimaryDiagnosis: Boolean
    $relatedPrimaryDiagnosis: ID
    $draft: Boolean
    $disease: String
    $majorVersion: Int
  ) {
    updateDiagnosis(
      id: $id
      diagnosisCodeId: $diagnosisCodeId
      stageType: $stageType
      stageTCodePathological: $stageTCodePathological
      stageNCodePathological: $stageNCodePathological
      stageMCodePathological: $stageMCodePathological
      stageTCodeClinical: $stageTCodeClinical
      stageNCodeClinical: $stageNCodeClinical
      stageMCodeClinical: $stageMCodeClinical
      morphology: $morphology
      laterality: $laterality
      otherInfo: $otherInfo
      diagnosisDate: $diagnosisDate
      isPrimaryDiagnosis: $isPrimaryDiagnosis
      relatedPrimaryDiagnosis: $relatedPrimaryDiagnosis
      draft: $draft
      disease: $disease
      majorVersion: $majorVersion
    ) {
      diagnosis {
        ...DiagnosisTypeWithTreatmentStarted
        relatedPrimaryDiagnosis {
          ...DiagnosisTypeWithTreatmentStarted
        }
      }
    }
  }
  ${DIAGNOSIS_TYPE_WITH_TREATMENT_STARTED}
`;

export const LOAD_STAGE_MORPHOLOGY_LIST = gql`
  query morphologyLookupList($tumourStream: String!) {
    morphologyLookupList(tumourStream: $tumourStream) {
      id
      displayName
    }
  }
`;

export const LOAD_STAGE_TNM_LIST = gql`
  query tnmLookupGroupList($groupNumber: String!) {
    tnmLookupGroupList(groupNumber: $groupNumber) {
      id
      mainCode
      subCode
      description
    }
  }
`;

export const GET_ICD10_CATEGORY_LIST = gql`
  query icd10($isPrimaryDiagnosis: Boolean!) {
    diagnosisCategoryList(isPrimaryDiagnosis: $isPrimaryDiagnosis) {
      tumourStream
      diagnosisList {
        id
        diagnosisCode
        diagnosisDescription
        stageNumber
        tumourStream {
          id
          name
        }
      }
    }
  }
`;

export const GET_ORDERED_DIAGNOSIS_LIST = gql`
  query diagnosisOrdered($patientId: ID!, $isPrimaryDiagnosis: Boolean) {
    diagnosisCodeList(patientId: $patientId, isPrimaryDiagnosis: $isPrimaryDiagnosis) {
      id
      diagnosisCode
      diagnosisDescription
      stageNumber
      tumourStream {
        id
        name
      }
    }
  }
`;

export const GET_CLINEX_CODES = gql`
  query clinexDiagnosis($patientId: ID!, $careplanId: ID) {
    clinexDiagnosisCode(patientId: $patientId, careplanId: $careplanId) {
      clinexUuid
      diagnosisList {
        id
        diagnosisCode
        diagnosisDescription
        stageNumber
        certainty
        tumourStream {
          id
          name
        }
      }
    }
  }
`;

export const CLINEX_DIAGNOSIS_DATA = gql`
  query clinexDiagnosisDataFull($diagnosisId: ID!, $careplanId: ID!) {
    clinexDiagnosisDataFull(diagnosisId: $diagnosisId, careplanId: $careplanId) {
      diagnosisData
    }
  }
`;

export const LOG_SIGMA_MUTATION = gql`
  mutation logSigma($data: String!) {
    sigmaLogEvent(data: $data) {
      success
    }
  }
`;

export const LOG_CLINEX_MUTATION = gql`
  mutation logClinex($data: String!) {
    clinexLogEvent(data: $data) {
      success
    }
  }
`;

export const GET_DISEASES = gql`
  query diseases($ajccMajorVersion: Int!, $relatedIcdCode: String) {
    diseases(ajccMajorVersion: $ajccMajorVersion, relatedIcdCode: $relatedIcdCode) {
      id
      diseaseId
      title
      chapter
    }
  }
`;

export const GET_MORPHOLOGY_LIST_BY_DISEASE = gql`
  query morphologyListByDisease($diseaseId: String!, $majorVersion: Int!) {
    morphologyListByDisease(diseaseId: $diseaseId, majorVersion: $majorVersion) {
      id
      displayName
    }
  }
`;

export const GET_TNM_LIST_BY_DISEASE = gql`
  query tnmListByDisease($diseaseId: String!, $majorVersion: Int!) {
    tnmListByDisease(diseaseId: $diseaseId, majorVersion: $majorVersion) {
      id
      mainCode
      subCode
      description
    }
  }
`;

export const GET_CAREPLAN_AND_DIAGNOSIS = gql`
  ${DIAGNOSIS_TYPE_WITH_TREATMENT_STARTED}
  query careplanAndDiagnosis($careplanId: ID!) {
    careplan(id: $careplanId) {
      id
      careplanStatus
      templateChangeRequested
      treatingDepartment
      submittedAt
      simulationModification
      prescriptionModification
      diagnosisModification
      cied
      ciedPatientDependancy
      ciedLinacRequired
      featureVersion
      diagnosis {
        ...DiagnosisTypeWithTreatmentStarted
        relatedPrimaryDiagnosis {
          ...DiagnosisTypeWithTreatmentStarted
        }
      }
      changeType
      parent {
        id
        careplanStatus
        changeType
        submittedAt
      }
      practitioner {
        id
      }
      isPrimDiagnosisOwner
      isMetsDiagnosisOwner
      ajccMajorVersion
    }
  }
`;

export const CREATE_OR_UPDATE_CAREPLAN_DIAGNOSIS = gql`
  mutation createOrUpdateCareplanDiagnosis(
    $patientId: ID!
    $careplanId: ID!
    $primaryDiagnosis: DiagnosisInputType
    $metastasisDiagnosis: DiagnosisInputType
  ) {
    createOrUpdateCareplanDiagnosis(
      patientId: $patientId
      careplanId: $careplanId
      primaryDiagnosis: $primaryDiagnosis
      metastasisDiagnosis: $metastasisDiagnosis
    ) {
      careplan {
        id
        isPrimDiagnosisOwner
        isMetsDiagnosisOwner
        diagnosis {
          ...DiagnosisTypeWithTreatmentStarted
          relatedPrimaryDiagnosis {
            ...DiagnosisTypeWithTreatmentStarted
          }
        }
      }
    }
  }
  ${DIAGNOSIS_TYPE_WITH_TREATMENT_STARTED}
`;

export const SUBMIT_DIAGNOSIS_ONLY = gql`
  mutation submitDiagnosisOnly($diagnosisId: ID!, $careplanId: ID!) {
    submitDiagnosisOnly(diagnosisId: $diagnosisId, careplanId: $careplanId) {
      success
    }
  }
`;
